import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import {RootState} from "@/store/Store";

export const SectionsSlice = createSlice({
    name: "SectionsSlice",
    initialState: getInitialState(),
    reducers: {
        // RESETS:
        reset: () => {
            return getInitialState();
        },
        resetSectionDialog: (state) => {
            const initialState = getInitialState();
            state.createOrEditSectionDialog = initialState.createOrEditSectionDialog;
        },
        resetBlockDialog: (state) => {
            const initialState = getInitialState();
            state.createOrEditBlockDialog = initialState.createOrEditBlockDialog;
        },
        resetDeleteSectionDialog: (state) => {
            const initialState = getInitialState();
            state.deleteSectionDialog = initialState.deleteSectionDialog;
        },
        resetDeleteBlockDialog: (state) => {
            const initialState = getInitialState();
            state.deleteBlockDialog = initialState.deleteBlockDialog;
        },
        resetViewDescriptionDialog: (state) => {
            const initialState = getInitialState();
            state.viewDescriptionDialog = initialState.viewDescriptionDialog;
        },

        // CHANGE FORM VISIBILITY
        changeSectionFormVisible: (
          state,
          action: PayloadAction<{ visible: boolean, variant: "new" | "edit", id?: string }>
        ) => {
            state.createOrEditSectionDialog.variant = action.payload.variant;
            state.createOrEditSectionDialog.visible = action.payload.visible;
            if (action.payload.id !== undefined) {
                state.createOrEditSectionDialog.id = action.payload.id;
            }
        },
        changeBlockFormVisible: (
          state,
          action: PayloadAction<{ visible: boolean, variant: "new" | "edit", id?: string }>
        ) => {
            state.createOrEditBlockDialog.variant = action.payload.variant;
            state.createOrEditBlockDialog.visible = action.payload.visible;
            if (action.payload.id !== undefined) {
                state.createOrEditBlockDialog.id = action.payload.id;
            }
        },
        changeDeleteSectionFormVisible: (state, action: PayloadAction<boolean>) => {
            state.deleteSectionDialog.visible = action.payload;
        },
        changeDeleteBlockFormVisible: (state, action: PayloadAction<boolean>) => {
            state.deleteBlockDialog.visible = action.payload;
        },
        showViewDescriptionDialog: (state, action: PayloadAction<{title: string, description: string | null}>) => {
            state.viewDescriptionDialog.visible = true;
            state.viewDescriptionDialog.title = action.payload.title;
            state.viewDescriptionDialog.description = action.payload.description;
        },
        closeViewDescriptionDialog: (state) => {
            state.viewDescriptionDialog.visible = false;
            state.viewDescriptionDialog.title = "";
            state.viewDescriptionDialog.description = null;
        },

        updateDeleteSectionFields: (
          state,
          action: PayloadAction<Omit<deleteSectionDialogInterface, "visible">>
        ) => {
            state.deleteSectionDialog = {
                ...state.deleteSectionDialog,
                ...action.payload,
            }
        },
        updateDeleteBlockFields: (
          state,
          action: PayloadAction<Omit<deleteBlockDialogInterface, "visible">>
        ) => {
            state.deleteBlockDialog = {
                ...state.deleteBlockDialog,
                ...action.payload,
            }
        }
    },
});

export const {
    reset,
    resetSectionDialog,
    resetBlockDialog,
    resetDeleteSectionDialog,
    resetDeleteBlockDialog,
    resetViewDescriptionDialog,
    changeSectionFormVisible,
    changeBlockFormVisible,
    changeDeleteSectionFormVisible,
    changeDeleteBlockFormVisible,
    showViewDescriptionDialog,
    closeViewDescriptionDialog,
    updateDeleteSectionFields,
    updateDeleteBlockFields,
} = SectionsSlice.actions;

export const SectionsSelectors = {
    createOrEditSectionDialog: (state: RootState) => state.sections.createOrEditSectionDialog,
    createOrEditBlockDialog: (state: RootState) => state.sections.createOrEditBlockDialog,
    deleteSectionDialog: (state: RootState) => state.sections.deleteSectionDialog,
    deleteBlockDialog: (state: RootState) => state.sections.deleteBlockDialog,
    viewDescriptionDialog: (state: RootState) => state.sections.viewDescriptionDialog,
}

function getInitialState(): SectionsSliceInitialStateInterface {
    return {
        createOrEditSectionDialog: {
            visible: false,
            variant: "new",
            id: "",
        },

        createOrEditBlockDialog: {
            visible: false,
            variant: "new",
            id: "",
        },

        deleteSectionDialog: {
            visible: false,
            id: "",
            title: "",
        },
        deleteBlockDialog: {
            visible: false,
            id: "",
            title: "",
            parentId: "",
        },

        viewDescriptionDialog: {
            visible: false,
            title: "",
            description: null,
        },
    }
}

interface createOrEditSectionDialogInterface {
    visible: boolean,
    variant: "new" | "edit",
    id: string,
}

interface createOrEditBlockDialogInterface {
    visible: boolean,
    variant: "new" | "edit",
    id: string,
}

interface deleteSectionDialogInterface {
    visible: boolean,
    id: string,
    title: string,
}

interface deleteBlockDialogInterface {
    visible: boolean,
    id: string,
    title: string,
    parentId: string,
}

interface viewDescriptionDialogInterface {
    visible: boolean,
    title: string,
    description: string | null,
}

interface SectionsSliceInitialStateInterface {
    createOrEditSectionDialog: createOrEditSectionDialogInterface,
    createOrEditBlockDialog: createOrEditBlockDialogInterface,
    deleteSectionDialog: deleteSectionDialogInterface,
    deleteBlockDialog: deleteBlockDialogInterface,
    viewDescriptionDialog: viewDescriptionDialogInterface,
}

export const SectionsReducer = SectionsSlice.reducer;
